import { DestroyRef, Directive, ElementRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * Scrolls its element back to the top when the route's path changes.
 *
 * The router's `scrollPositionRestoration: 'top'` only scrolls the window. A layout
 * that scrolls an inner container (the client portal's `.content-wrapper`, which does
 * so to keep its sidebar sticky) never moves, so a link clicked at the bottom of one
 * page opened the next page at that same depth. Put this on that container.
 *
 * Only a change of path counts: query-param and fragment changes (list filters,
 * paging, a dashboard's `?range=`) keep the reader where they are.
 */
@Directive({
    selector: '[scrollTopOnNavigate]',
    standalone: true,
})
export class ScrollTopOnNavigateDirective {
    constructor() {
        const el = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
        const router = inject(Router);
        let lastPath = pathOf(router.url);

        router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                takeUntilDestroyed(inject(DestroyRef)),
            )
            .subscribe((e) => {
                const path = pathOf(e.urlAfterRedirects);
                if (path !== lastPath) {
                    el.scrollTop = 0;
                }
                lastPath = path;
            });
    }
}

/** A router URL without its query string and fragment. */
function pathOf(url: string): string {
    return url.split(/[?#]/)[0];
}
